import { useEffect, useState, useMemo } from "react";
import { supabase } from "../../supabase";
import { FileEntityTypeWithUrl } from "../../../dentlab/src/hooks/useStorage";
import { FileEntityType } from "../../supabase/supabaseTypes";
import { Logger } from "../../logger/Logger";
import { useFileUrlCacheStore } from "../../store/file-url-cache.store";

export const useFileHandler = (fileIds: string[]) => {
	const [fileMeta, setFileMeta] = useState<FileEntityType[]>([]);
	const [loading, setLoading] = useState(true);
	const [sharedUntil, setSharedUntil] = useState<
		Record<string, string | null>
	>({});

	const memoizedFileIds = useMemo(() => fileIds, [JSON.stringify(fileIds)]);

	const fileCache = useFileUrlCacheStore((state) => state.fileCache);

	useEffect(() => {
		const loadFiles = async () => {
			try {
				if (memoizedFileIds.length === 0) {
					setFileMeta([]);
					setLoading(false);
					return;
				}

				setLoading(true);

				const [filesResult, sharesResult] = await Promise.all([
					supabase
						.from("files")
						.select("*")
						.in("id", memoizedFileIds),
					supabase
						.from("shared")
						.select("*")
						.in("file_id", memoizedFileIds),
				]);

				if (filesResult.error || !filesResult.data) {
					throw new Error("Failed to fetch file data from database");
				}

				// A file can have several share rows (re-shared, other recipients); an active one wins.
				const sharedUntilMap: Record<string, string | null> = {};
				sharesResult.data?.forEach((share) => {
					if (!share.file_id) return;
					const isActive =
						!share.shared_until ||
						new Date(share.shared_until) > new Date();
					sharedUntilMap[share.file_id] =
						sharedUntilMap[share.file_id] === null || isActive
							? null
							: share.shared_until;
				});
				setSharedUntil(sharedUntilMap);
				setFileMeta(filesResult.data);

				// Use the shared URL cache — skips files already cached
				await useFileUrlCacheStore
					.getState()
					.preloadFiles(memoizedFileIds);
			} catch (error) {
				Logger.error("Error loading files", {}, error);
			} finally {
				setLoading(false);
			}
		};

		loadFiles();

		const subscriptions = memoizedFileIds.map((fileId) => {
			return supabase
				.channel(`shared:file_id=eq.${fileId}`)
				.on(
					"postgres_changes",
					{
						event: "*",
						schema: "public",
						table: "shared",
						filter: `file_id=eq.${fileId}`,
					},
					(payload) => {
						if (
							payload.new &&
							typeof payload.new === "object" &&
							"shared_until" in payload.new &&
							"file_id" in payload.new
						) {
							setSharedUntil((prev) => ({
								...prev,
								[(payload.new as any).file_id]: (
									payload.new as any
								).shared_until,
							}));

							if ((payload.new as any).shared_until === null) {
								loadFiles();
							}
						}
					}
				)
				.subscribe();
		});

		return () => {
			subscriptions.forEach((subscription) => {
				supabase.removeChannel(subscription);
			});
		};
	}, [memoizedFileIds]);

	const files: FileEntityTypeWithUrl[] = useMemo(() => {
		return fileMeta.map((file) => {
			const cached = fileCache[file.id];
			return {
				...file,
				url: cached?.signed_url ?? "",
			};
		});
	}, [fileMeta, fileCache]);

	return {
		files,
		loading,
		sharedUntil,
	};
};
